import { ROLE_LABELS } from '@natashu/shared';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { BrandLogo } from '../components/brand/BrandLogo.tsx';
import { Avatar } from '../components/ui/Avatar.tsx';
import { IconButton } from '../components/ui/Button.tsx';
import { Tooltip } from '../components/ui/Tooltip.tsx';
import { useCriticalAlertCount } from '../features/system/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { cx } from '../lib/cx.ts';
import type { NavSection } from './nav.ts';
import { sectionMatches, useNavSections } from './useNavSections.ts';

export interface SidebarNavProps {
  /** Icon-only rail (collapsed on a computer, always on an iPad). */
  rail: boolean;
  onToggleRail?: () => void;
  /** Present when rendered inside the mobile drawer. */
  onClose?: () => void;
}

/** One entry per job; a section's other pages are tabs on the page itself (SectionTabs). */
export function SidebarNav({ rail, onToggleRail, onClose }: SidebarNavProps) {
  const { me, can } = useAuth();
  const sections = useNavSections();
  const { pathname } = useLocation();
  const criticalAlerts = useCriticalAlertCount(can('system.manage'));

  const entry = (section: NavSection) => {
    const Icon = section.icon;
    const active = sectionMatches(section, pathname);
    const badge = section.key === 'settings' && criticalAlerts > 0 ? criticalAlerts : 0;
    const link = (
      <Link to={section.items[0]!.to} className={cx('nav-item', active && 'is-active')} aria-current={active ? 'page' : undefined} title={rail ? undefined : section.description}>
        <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
        <span className="nav-item__label">{section.label}</span>
        {badge > 0 && (
          <span className="nav-item__badge num" data-testid="nav-critical-alerts">
            <span aria-hidden="true">{badge > 99 ? '99+' : badge}</span>
            <span className="visually-hidden">มีการแจ้งเตือนวิกฤต {badge} รายการ</span>
          </span>
        )}
      </Link>
    );
    return (
      <li key={section.key}>
        {rail ? (
          <Tooltip content={badge > 0 ? `${section.label} (วิกฤต ${badge})` : section.label} side="right" describeChild={false}>
            {link}
          </Tooltip>
        ) : (
          link
        )}
      </li>
    );
  };

  const daily = sections.filter((s) => !s.footer);
  const footer = sections.filter((s) => s.footer);

  return (
    <div className={cx('sidebar__inner', rail && 'is-rail')}>
      <div className="sidebar__brand">
        <Link to="/" className="sidebar__brand-link" aria-label="NATASHU Chat & CRM — หน้าแรก">
          <BrandLogo size={44} variant="plain" decorative className="sidebar__logo" />
          <span className="sidebar__brand-text" aria-hidden="true">
            <span className="sidebar__brand-name">NATASHU</span>
            <span className="sidebar__brand-sub">Chat &amp; CRM</span>
          </span>
        </Link>
        {onClose && <IconButton icon={X} label="ปิดเมนู" onClick={onClose} tooltip={false} />}
      </div>

      {onToggleRail && (
        <Tooltip content={rail ? 'ขยายเมนู' : 'ย่อเมนู'} side="right" describeChild={false} disabled={!rail}>
          <button type="button" className="nav-item sidebar__rail-toggle" aria-expanded={!rail} onClick={onToggleRail}>
            {rail ? <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden="true" /> : <PanelLeftClose size={18} strokeWidth={1.75} aria-hidden="true" />}
            <span className="nav-item__label">{rail ? 'ขยายเมนู' : 'ย่อเมนู'}</span>
          </button>
        </Tooltip>
      )}

      <nav className="sidebar__nav" aria-label="เมนูหลัก">
        <div className="nav-group">
          <ul>{daily.map(entry)}</ul>
        </div>
        {footer.length > 0 && (
          <div className="nav-group nav-group--footer">
            <ul>{footer.map(entry)}</ul>
          </div>
        )}
      </nav>

      <div className="sidebar__footer">
        {me && (
          <Link to="/account/security" className="sidebar-user" aria-label={`บัญชีของฉัน: ${me.user.displayName}`}>
            <Avatar name={me.user.displayName} size={34} />
            <span className="sidebar-user__text" aria-hidden="true">
              <span className="sidebar-user__name">{me.user.displayName}</span>
              <span className="sidebar-user__role">{ROLE_LABELS[me.user.role]?.th ?? me.user.role}</span>
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}
